import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { enquiriesApi } from '../api/endpoints.js';
import { friendlyMessage } from '../api/ApiError.js';
import Field from '../shared/Field.jsx';
import Spinner from '../shared/Spinner.jsx';
import { school } from '../constants/school.js';

/*
 * Mirrors CreateEnquiryDto on the server. The server validates independently —
 * this copy exists so a parent finds out about a typo before a round trip, not
 * so the server can trust the browser.
 */
const schema = z.object({
  parentName: z
    .string()
    .trim()
    .min(2, 'Please tell us your name')
    .max(120, 'That is longer than we can store'),
  email: z
    .string()
    .trim()
    .min(1, 'We need an email address to reply to')
    .email('That does not look like an email address')
    .max(180),
  phone: z.string().trim().max(40, 'That is longer than we can store').optional(),
  childName: z.string().trim().min(1, "Please tell us your child's first name").max(120),
  childDateOfBirth: z
    .string()
    .trim()
    .optional()
    .refine(
      (value) => !value || new Date(value) <= new Date(),
      'That date is in the future',
    ),
  stage: z.enum(['toddler-room', 'primary', 'secondary'], {
    message: 'Please choose a stage',
  }),
  preferredStart: z.string().trim().max(60).optional(),
  message: z
    .string()
    .trim()
    .max(2000, 'Please keep this under 2000 characters')
    .optional(),
  // The honeypot. Never validated against — whatever a bot puts here is passed
  // to the server, which decides what to do with it.
  website: z.string().max(200).optional(),
});

const STAGES = [
  { value: 'toddler-room', label: 'The Toddler Room (ages 2–4)' },
  { value: 'primary', label: 'Primary School (Grades 1–5)' },
  { value: 'secondary', label: 'Secondary School (Grades 6–10)' },
];

export default function EnquiryForm() {
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const successRef = useRef(null);

  /*
   * Move focus to the confirmation once it renders. Doing this in the submit
   * handler instead would read the ref before React had committed the new
   * element — and would leave a screen reader user sitting on a submit button
   * that has just been replaced, with no announcement that anything happened.
   */
  useEffect(() => {
    if (submitted) successRef.current?.focus();
  }, [submitted]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: zodResolver(schema),
    // Complain on blur rather than on every keystroke: telling someone their
    // email is invalid while they are still typing the local part is hostile.
    mode: 'onBlur',
    defaultValues: { stage: '' },
  });

  const onSubmit = async (values, event) => {
    setSubmitError(null);

    /*
     * The honeypot is read from the live DOM rather than from form state.
     * A bot that assigns `input.value = '…'` without dispatching an input
     * event never reaches react-hook-form's state, so trusting `values.website`
     * alone would let exactly the crudest kind of bot through.
     */
    const trap = event?.target?.elements?.website?.value || values.website || undefined;

    try {
      await enquiriesApi.submit({
        ...values,
        // Strip the optional empties: the API rejects nulls it did not ask for.
        phone: values.phone || undefined,
        childDateOfBirth: values.childDateOfBirth || undefined,
        preferredStart: values.preferredStart || undefined,
        message: values.message || undefined,
        website: trap,
      });

      setSubmitted(true);
      reset();
    } catch (error) {
      setSubmitError(friendlyMessage(error));
    }
  };

  if (submitted) {
    return (
      <div
        ref={successRef}
        tabIndex={-1}
        className="rounded-3xl border-[3px] border-forest bg-mint p-8 text-center"
      >
        <p className="font-hand text-2xl text-coral">Got it</p>
        <h3 className="mt-1 font-display text-[28px] font-extrabold text-forest">
          Your enquiry is with us
        </h3>
        <p className="mx-auto mt-3 max-w-[46ch] text-[16px] leading-[1.6] text-ink-soft">
          Someone from admissions replies within two working days. If it is urgent, call{' '}
          <a
            className="font-bold text-forest underline"
            href={`tel:${school.phone.replace(/\s/g, '')}`}
          >
            {school.phone}
          </a>{' '}
          — a person answers, 8am–5pm.
        </p>
        <button
          type="button"
          onClick={() => setSubmitted(false)}
          className="mt-5 cursor-pointer text-[15px] font-bold text-forest underline"
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      aria-labelledby="enquiry-form-heading"
      className="rounded-3xl border-[3px] border-forest bg-white p-6 sm:p-8"
    >
      <h3
        id="enquiry-form-heading"
        className="font-display text-[26px] font-extrabold text-forest"
      >
        Send an enquiry
      </h3>
      <p className="mt-1.5 text-[15px] text-ink-soft">
        Two working days for a reply. Fields marked{' '}
        <span className="font-bold text-coral">*</span> are the ones we cannot do without.
      </p>

      {/*
        Honeypot. Hidden from sight, from the tab order and from the
        accessibility tree, so nobody using the page can fill it in — but a bot
        that fills every input will. The server drops those submissions
        silently. `sr-only` would not do: screen readers would read it out.
      */}
      <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
        <label htmlFor="website-field">Leave this empty</label>
        <input
          id="website-field"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          {...register('website')}
        />
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <Field
          label="Your name"
          required
          autoComplete="name"
          placeholder="Ifeoma Okafor"
          error={errors.parentName?.message}
          {...register('parentName')}
        />
        <Field
          label="Email"
          required
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@example.com"
          error={errors.email?.message}
          {...register('email')}
        />
        <Field
          label="Phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="0800 114 2255"
          error={errors.phone?.message}
          {...register('phone')}
        />
        <Field
          label="Child's first name"
          required
          autoComplete="off"
          placeholder="Chidi"
          error={errors.childName?.message}
          {...register('childName')}
        />
        <Field
          label="Child's date of birth"
          type="date"
          hint="Helps us point you at the right stage."
          error={errors.childDateOfBirth?.message}
          {...register('childDateOfBirth')}
        />
        <Field
          label="Which stage"
          required
          as="select"
          error={errors.stage?.message}
          {...register('stage')}
        >
          <option value="">Choose a stage…</option>
          {STAGES.map((stage) => (
            <option key={stage.value} value={stage.value}>
              {stage.label}
            </option>
          ))}
        </Field>
        <Field
          label="Preferred start"
          placeholder="September 2026"
          className="sm:col-span-2"
          error={errors.preferredStart?.message}
          {...register('preferredStart')}
        />
        <Field
          label="Anything else"
          as="textarea"
          rows={4}
          placeholder="We would like to visit on a Tuesday."
          className="sm:col-span-2"
          error={errors.message?.message}
          {...register('message')}
        />
      </div>

      {/*
        Announced when it appears rather than only being visible — a submission
        that failed for someone not looking at this corner of the page is
        otherwise completely silent.
      */}
      <div aria-live="polite" className="mt-4 empty:mt-0">
        {submitError && (
          <p className="rounded-2xl border-2 border-coral bg-coral/10 px-4 py-3 text-[15px] font-bold text-coral-deep">
            {submitError}
          </p>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-full bg-forest px-[30px] py-[15px] font-display text-lg font-bold text-white shadow-[0_5px_0_var(--color-forest-deep)] transition hover:translate-y-[3px] hover:shadow-[0_2px_0_var(--color-forest-deep)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-[0_5px_0_var(--color-forest-deep)]"
        >
          {isSubmitting ? 'Sending…' : 'Send enquiry'}
        </button>

        {isSubmitting && <Spinner label="Sending your enquiry" size={22} />}

        <p className="text-[14px] leading-[1.5] text-ink-soft">
          We use this only to reply to you.{' '}
          <Link className="font-bold text-forest underline" to="/privacy">
            What we do with it
          </Link>
          .
        </p>
      </div>
    </form>
  );
}
